import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Type, FileText, Layout } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TextSizeCalculatorPage/components/CalculationForm';
import ResultsOverview from './TextSizeCalculatorPage/components/ResultsOverview';
import TextPreview from './TextSizeCalculatorPage/components/TextPreview';
import AccessibilityGuidelines from './TextSizeCalculatorPage/components/AccessibilityGuidelines';
import QuickAdjustmentSidebar from './TextSizeCalculatorPage/components/QuickAdjustmentSidebar';
import CommonSizesSidebar from './TextSizeCalculatorPage/components/CommonSizesSidebar';
import TypographyTipsSidebar from './TextSizeCalculatorPage/components/TypographyTipsSidebar';
import InfoContent from './TextSizeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  textContent: 'Пример текста для проверки размера и читабельности. Эта строка поможет оценить, как выглядит ваш текст с выбранными параметрами шрифта в различных контекстах.',
  fontSize: '16',
  fontSizeUnit: 'px',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: '400',
  lineHeight: '1.5',
  letterSpacing: '0',
  textAlign: 'left',
  mediaType: 'screen',
  targetAudience: 'general',
  viewingDistance: '50',
  colorScheme: 'light',
  fontScale: '100',
  deviceType: 'desktop',
  russianStandard: 'GOST-R-54723',
  includeAccessibility: true
};

// Определение типов устройств и их характеристик
export const deviceTypes = [
  { id: 'desktop', name: 'Компьютер', defaultFontSize: 16, defaultWidth: 1920 },
  { id: 'laptop', name: 'Ноутбук', defaultFontSize: 14, defaultWidth: 1366 },
  { id: 'tablet', name: 'Планшет', defaultFontSize: 14, defaultWidth: 768 },
  { id: 'mobile', name: 'Смартфон', defaultFontSize: 12, defaultWidth: 375 },
  { id: 'tv', name: 'Телевизор/Smart TV', defaultFontSize: 20, defaultWidth: 1920 },
  { id: 'print', name: 'Печатная продукция', defaultFontSize: 12, defaultWidth: 595 }
];

// Определение семейств шрифтов
export const fontFamilies = [
  { id: 'Inter, system-ui, sans-serif', name: 'Inter', type: 'sans-serif' },
  { id: 'Arial, Helvetica, sans-serif', name: 'Arial', type: 'sans-serif' },
  { id: '"Roboto", sans-serif', name: 'Roboto', type: 'sans-serif' },
  { id: '"PT Sans", sans-serif', name: 'PT Sans', type: 'sans-serif' },
  { id: '"Open Sans", sans-serif', name: 'Open Sans', type: 'sans-serif' },
  { id: '"Georgia", serif', name: 'Georgia', type: 'serif' },
  { id: '"Times New Roman", Times, serif', name: 'Times New Roman', type: 'serif' },
  { id: '"PT Serif", serif', name: 'PT Serif', type: 'serif' },
  { id: '"Courier New", monospace', name: 'Courier New', type: 'monospace' },
  { id: '"Consolas", monospace', name: 'Consolas', type: 'monospace' }
];

// Российские стандарты типографики
export const russianStandards = [
  { id: 'GOST-R-54723', name: 'ГОСТ Р 54723-2019', desc: 'Интернет-ресурсы и электронные издания' },
  { id: 'GOST-R-58511', name: 'ГОСТ Р 58511-2019', desc: 'Издания. Требования к шрифтовому оформлению' },
  { id: 'GOST-P-57850', name: 'ГОСТ Р 57850-2017', desc: 'Оформление сайтов для слабовидящих' },
  { id: 'SanPin', name: 'СанПиН 1.2.3685-21', desc: 'Гигиенические нормативы для электронных изданий' }
];

// Целевая аудитория
export const targetAudiences = [
  { id: 'general', name: 'Общая аудитория' },
  { id: 'elderly', name: 'Пожилые люди' },
  { id: 'children', name: 'Дети и подростки' },
  { id: 'visually-impaired', name: 'Люди с нарушениями зрения' },
  { id: 'dyslexic', name: 'Люди с дислексией' }
];

/**
 * TextSizeCalculatorPage Component - Калькулятор размера текста с учетом различных факторов и
 * требований доступности, стандартов типографики и российских нормативов.
 */
const TextSizeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('text-size-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор размера текста | Gensio';
  const metaDescription = tool?.metaDescription || 'Оптимизируйте размер текста для веб-сайтов, приложений и печатных материалов согласно российским стандартам, требованиям доступности и типографики.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedPresets, setSavedPresets] = useState([]);
  const [activeTab, setActiveTab] = useState('preview');

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных пресетов из localStorage ---
  useEffect(() => {
    const savedPresetsData = localStorage.getItem('savedTextPresets');
    if (savedPresetsData) {
      try {
        setSavedPresets(JSON.parse(savedPresetsData));
      } catch (e) {
        console.error("Error loading saved presets:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'accessibility':
          newState.includeAccessibility = true;
          newState.targetAudience = 'visually-impaired';
          newState.fontSize = '20';
          break;
        case 'mobile':
          newState.deviceType = 'mobile';
          newState.fontSize = '14';
          break;
        case 'print':
          newState.deviceType = 'print';
          newState.mediaType = 'print';
          newState.fontSize = '12';
          newState.fontFamily = '"Times New Roman", Times, serif';
          break;
        case 'gost':
          newState.russianStandard = 'GOST-R-58511';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение пресета в localStorage
  const savePreset = (name) => {
    if (!results) return;

    const newSavedPreset = {
      id: Date.now().toString(),
      name: name || `Пресет от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedPresets = [...savedPresets, newSavedPreset];
    setSavedPresets(updatedSavedPresets);
    localStorage.setItem('savedTextPresets', JSON.stringify(updatedSavedPresets));

    return newSavedPreset;
  };

  // Удаление сохраненного пресета
  const deleteSavedPreset = (id) => {
    const updatedSavedPresets = savedPresets.filter(preset => preset.id !== id);
    setSavedPresets(updatedSavedPresets);
    localStorage.setItem('savedTextPresets', JSON.stringify(updatedSavedPresets));
  };

  // Загрузка сохраненного пресета
  const loadSavedPreset = (id) => {
    const presetToLoad = savedPresets.find(preset => preset.id === id);
    if (presetToLoad) {
      setFormData(presetToLoad.formData);
      setResults(presetToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Type className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор размера текста'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              fontFamilies={fontFamilies}
              deviceTypes={deviceTypes}
              russianStandards={russianStandards}
              targetAudiences={targetAudiences}
              savePreset={savePreset}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <div className="flex items-center border-b border-gray-200 dark:border-gray-700 mb-6">
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`py-3 px-4 text-sm font-medium rounded-t-lg ${
                      activeTab === 'preview'
                        ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Предпросмотр текста
                  </button>
                  <button
                    onClick={() => setActiveTab('accessibility')}
                    className={`py-3 px-4 text-sm font-medium rounded-t-lg ${
                      activeTab === 'accessibility'
                        ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Рекомендации по доступности
                  </button>
                </div>
                
                <ResultsOverview results={results} />
                
                {activeTab === 'preview' ? (
                  <TextPreview 
                    formData={formData}
                    results={results}
                  />
                ) : (
                  <AccessibilityGuidelines 
                    formData={formData}
                    results={results}
                  />
                )}
              </>
            )}

            {/* --- Информационный контент о размерах текста --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель стандартных размеров --- */}
            <CommonSizesSidebar 
              savedPresets={savedPresets}
              deleteSavedPreset={deleteSavedPreset}
              loadSavedPreset={loadSavedPreset}
            />

            {/* --- Панель советов по типографике --- */}
            <TypographyTipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Text Preview Styling */
        .text-preview-panel {
          transition: all 0.2s ease-in-out;
        }
        
        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default TextSizeCalculatorPage;